import { Component, Input, OnChanges, OnInit, OnDestroy, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, map, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import moment from 'moment';
import Tabulator from 'tabulator-tables';
import { TableSchema } from '../../models/table-schema';
import { TableService } from '../../services/table.service';
import { ModalService } from '../../modal'
import { ToastService } from 'src/app/services/toast.service';
import { BehaviorSubject } from 'rxjs';
import { DisplayService } from 'src/app/services/display.service';
import { MyAccess } from 'src/app/models/my-access';
import { AdminService } from 'src/app/services/admin.service';
import { StringLocalizerService } from 'src/app/services/string-localizer.service';
import { TableRow } from 'src/app/models/table-row';
import { Parser } from 'src/app/helpers/parser';
import { FilterStateService } from '../../models/filter-states';
import { createDataWorker } from './worker-factory';
import { LoggerService } from 'src/app/services/logger.service';

@Component({
    selector: 'app-tabulator-user-table',
    templateUrl: './tabulator-user-table.component.html',
    styleUrls: ['./tabulator-user-table.component.css'],
    standalone: false
})
export class TabulatorUserTableComponent implements OnChanges, OnInit, OnDestroy {
  private readonly logger = inject(LoggerService);
  private destroy$ = new Subject<void>();
  private dataWorker: Worker | null = null;
  @Input() tableInfo: any;
  files: any[] = [];
  schema: TableSchema;
  tab: HTMLDivElement = null;
  tabHeaders: any[] = [];
  tabRows: any[] = [];
  table: Tabulator;
  addRowShow: boolean = true;
  duplicateSelectedRowsShow: boolean = true;
  deleteSelectedRowsShow: boolean = true;
  undeleteSelectedRowsShow: boolean = true;
  editSelectedRowsShow: boolean = true;
  saveShow: boolean = true;
  public tableIdX = new BehaviorSubject<string>("");
  public $tableIdX = this.tableIdX.asObservable();
  public drawn: boolean = false;
  public tableId: string = '';
  public rowStateCounts = new BehaviorSubject<{ edited: number; new: number; deleted: number }>({ edited: 0, new: 0, deleted: 0 });
  $rowChangesLength: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public tableGrayout: boolean = false;
  public showOriginal: boolean = true;
  public showChanged: boolean = true;
  public showNew: boolean = true;
  public showDeleted: boolean = false;
  public showNewDeleted: boolean = false;
  public showSelectedRecords: boolean = false;
  public showOriginalToolTipKey: string = "hideOriginalRecords";
  public showChangedToolTipKey: string = "hideEditedOriginalRecords";
  public showNewToolTipKey: string = "hideNewRecords";
  public showDeletedToolTipKey: string = "showDeletedRecords";
  public showNewDeletedToolTipKey: string = "showDeletedNewRecords";
  public allowEdit: boolean = false;
  public myAccess: MyAccess | null = null;
  public currentPage: number = 1;
  public pageSize: number = 25;
  public totalPages = 0;
  private filterStateService: FilterStateService;
  public sorter = '';
  public headerFilterValues = [];
  public rowChanges = [];
  public pageRange: number[] = [];
  public filterParams = "";
  public strings: {} = {};
  public sortParam = "";
  public tabulatorLangObject: {} = {};
  filterIsActive: boolean = false;
  private colByName = new Map<string, any>();
  private lastCommittedHeaderFilters: Record<string, any> = {};
  private readonly DATE_FILTER_FORMATS = [
    "YYYY-MM-DDTHH:mm:ss",
    "YYYY-MM-DD",
    "MM/DD/YYYY",
    "M/D/YYYY",
    "MM-DD-YYYY",
    "M-D-YYYY",
    "MM/DD/YY",
    "M/D/YY",
    "MM-DD-YY",
    "M-D-YY"
  ];
  constructor(
    private toastService: ToastService,
    private tableService: TableService,
    private modalService: ModalService,
    private displayService: DisplayService,
    private adminService: AdminService,
    private stringLocalizerService: StringLocalizerService) {
    (window as any).moment = moment;
    this.filterStateService = new FilterStateService();
  }
  checkIfAnyFilterIsActive(): boolean {
    const filterState = this.filterStateService.getFilterState();
    const isSorterApplied = this.filterStateService.isSorterApplied();
    const hasCommittedServerFilter = !!this.filterParams || !!this.sorter; 
    return Object.values(filterState).some(v => v !== null && v !== '') || isSorterApplied || hasCommittedServerFilter;
  }

  ngOnInit(): void {
    this.stringLocalizerService.strings
      .pipe(
        filter(Boolean),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.strings = this.stringLocalizerService.getSection("tables");
        this.tabulatorLangObject = this.stringLocalizerService.getTabulatorLanguageObject();
      });

    this.tableService.rowChanges
      .pipe(
        map(rows => rows?.length ?? 0),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(len => {
        this.$rowChangesLength.next(len);
        this.showSelectedRecords = len > 0;
      });

    this.adminService.$myAccess
      .pipe(
        filter((access): access is MyAccess => access !== null),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(myAccess => {
        this.myAccess = myAccess;
        this.recalculateAllowEdit();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.dataWorker?.terminate();
    this.dataWorker = null;
    if (this.table) {
      this.table.destroy();
      this.table = null;
    }
  }

  async ngOnChanges() {
    if (!this.tableInfo) {
      this.displayService.setSelectedSpanText("");
      return;
    }
    this.dataWorker?.terminate();
    this.dataWorker = null;
    const currentId = String(this.tableInfo.id);
    this.tableService.ensureTracking(currentId);
    if (this.table) {
      try { this.table.clearHeaderFilter(); } catch { }
      try { this.table.clearSort(); } catch { }
    }
    if (!this.tableService.selectedTable || this.tableService.selectedTable.id !== currentId) {
      this.tableService.selectTable(currentId);
      await this.tableService.loadSelectedTable();
    }
    const serviceTable = this.tableService.selectedTable;
    const table: any = { ...serviceTable };
    this.seedDefaultRegistryFromInitial(serviceTable);
    this.tableId = `tab-${table?.id ?? this.tableInfo.id}`;
    this.rowStateCounts.next({ edited: 0, new: 0, deleted: 0 });
    this.$rowChangesLength.next(0);
    this.showSelectedRecords = false;

    this.currentPage = 1;
    this.totalPages = this.tableService.totalPages;
    this.pageSize = 25;
    this.recalculateAllowEdit();
    if (typeof Worker !== "undefined") {
      this.dataWorker = createDataWorker();

      this.dataWorker.onmessage = ({ data }) => {
        this.schema = table.schema;
        this.colByName.clear();
        (this.schema?.columns || []).forEach(c => this.colByName.set(c.columnName, c));
        this.tabHeaders = this.buildHeaders(this.schema, table.id);
        this.tabRows = this.applySchemaDefaultsToRows(data);
        this.redraw();
        this.drawn = true;
        this.dataWorker?.terminate();
        this.dataWorker = null;
        this.updatePageRange();
      };
      this.dataWorker.postMessage(JSON.parse(JSON.stringify(table)));
      return;
    }
    this.schema = table.schema;
    this.colByName.clear();
    (this.schema?.columns || []).forEach(c => this.colByName.set(c.columnName, c));
    this.tabHeaders = this.buildHeaders(this.schema, table.id);
    this.tabRows = this.applySchemaDefaultsToRows(serviceTable?.rows ?? []);
    this.redraw();
    this.drawn = true;
    this.updatePageRange();
  }

  // draws the table and sets up row/cell/column/table stylizations
  private drawTable(): void {
    if (this.table) {
      this.table.destroy();
      this.table = null;
    }
    if (this.tab != null) {
      this.tab.remove();
      this.tab = null;
    }
    this.tab = document.createElement("div");
    this.table = new Tabulator(this.tab, {
      rowFormatter: this.rowStateFormatter,
      cellClick: function (_e, cell) {
        if (typeof cell.valueAtLoad === "undefined") {
          cell.valueAtLoad = cell.getValue();
        }
      },
      cellEdited: (cell) => {
        const rowComponent = cell.getRow();
        const rowData = rowComponent.getData();
        const row: any = (cell as any)._cell.row;
        const columnName = cell.getColumn().getField();
        const rowId = rowData?.id;
        const col = this.colByName.get(columnName) || this.schema?.columns?.find(c => c.columnName === columnName);
        if (rowId && col) {
          const equalsDefault = (this.tableService as any).equalsDefaultPublic?.bind(this.tableService);
          const parseDefault = (this.tableService as any).parseDefaultForColumnPublic?.bind(this.tableService);
          const el = cell.getElement();
          const newVal = cell.getValue();

          const isCleared = newVal === null || newVal === "";
          if (isCleared) {
            if (this.shouldAutoRestore(col)) {
              const defVal = parseDefault?.(col);
              if (defVal !== null && typeof defVal !== 'undefined' && defVal !== "") {
                cell.setValue(defVal, true);
                this.tableService.markDefault(rowId, col.columnName);
                el.classList.add('is-default');
                el.setAttribute('title', 'Default from schema');
              } else {
                this.tableService.clearDefault(rowId, col.columnName);
                el.classList.remove('is-default');
                el.removeAttribute('title');
              }
            } else {
              this.tableService.markDefault(rowId, col.columnName);
              el.classList.add('is-default');
              el.setAttribute('title', 'Default from schema');
              try { cell.render(); } catch { }
            }
          } else {
            if (equalsDefault?.(col, newVal)) {
              this.tableService.markDefault(rowId, col.columnName);
              el.classList.add('is-default');
              el.setAttribute('title', 'Default from schema');
              try { cell.render(); } catch { }
            } else {
              this.tableService.clearDefault(rowId, col.columnName);
              el.classList.remove('is-default');
              el.removeAttribute('title');
            }
          }
        }
        // 3) Change tracking / baselines
        const rowState: number = rowData["rowState"];
        const baseline =
          (typeof cell._cell.saveBaseline !== "undefined")
            ? cell._cell.saveBaseline
            : (typeof (cell as any).valueAtLoad !== "undefined"
              ? (cell as any).valueAtLoad
              : cell._cell.initialValue);

        const newVal = cell.getValue();
        row.changedCells = Array.isArray(row.changedCells) ? row.changedCells : [];
        const normalize = (v: any) => (v === "" ? null : v);

        if (normalize(newVal) !== normalize(baseline)) {
          if (!row.changedCells.includes(columnName)) row.changedCells.push(columnName);
        } else {
          row.changedCells = row.changedCells.filter((c: string) => c !== columnName);
        }

        // 4) Row-state transitions (unchanged from your logic)
        switch (rowState) {
          case 0:
          case 1:
            if (row.changedCells.length >= 1) {
              if (row.data["rowState"] != 2) {
                row.data["rowState"] = 3;
                row.getElement().style.backgroundColor = "#A6A6DF";
              }
            }
            break;
          case 2:
            break;
          case 3:
            if (row.changedCells.length == 0) {
              if (row.data["rowState"] != 2) {
                row.data["rowState"] = 1;
                row.getElement().style.backgroundColor = "";
              }
            }
            break;
          case 4:
          case 5:
            break;
        }

        // 5) Validation and service notification (do these once at the end)
        this.editRow(rowData);
        if (!cell.isValid()) this.validateTableBeforeAction();
      },
      validationMode: "highlight",
      layout: "fitDataStretch",
      movableColumns: true,
      height: "100%",
      data: this.tabRows,
      columns: this.tabHeaders,
      locale: this.stringLocalizerService.currentCulture,
      langs: this.tabulatorLangObject,
      rowSelected: (row) => {
        this.tableService.addRowChange(row);
      },
      rowDeselected: (row) => {
        this.tableService.removeRowById(row._row.data.id);
      },
    });
    this.updateFilters();
    const host = document.getElementById(this.tableId);
    if (host) host.appendChild(this.tab);
    this.displayService.setSelectedSpanText(this.tableInfo.alias);
  }

  redraw() {
    this.drawTable();
  }

  // Deals with get rowSelection to work correctly 
  createAndAppendCheckbox(cell, onRendered) {
    let checkbox = this.createCheckbox("headerCheckbox");
    onRendered(() => {
      cell.getElement().appendChild(checkbox);
    });
    checkbox.addEventListener("change", this.handleCheckboxChange.bind(this));
  }

  createCheckbox(id: string) {
    let checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.id = id;
    return checkbox;
  }

  handleCheckboxChange(e) {
    const rows = this.table.getRows();
    if (e.target.checked) {
      rows.forEach(rowComponent => this.tableService.addRowChange(rowComponent));
      this.table.selectRow();
    } else {
      rows.forEach(rowComponent => this.tableService.removeRowById(rowComponent._row.data.id));
      this.table.deselectRow();
    }
  }
  // formats the row based on its rowState and marks cells that were filled by schema defaults
  private applyBooleanDefaultClass(cell: any) {
    const field = cell.getColumn()?.getField?.();
    const col = this.colByName.get(field) || this.schema?.columns?.find(c => c.columnName === field);
    if (!col || col.columnDataType !== 4) return;

    const value = cell.getValue();
    const isDef = this.tableService.equalsDefaultPublic(col, value);
    const el: HTMLElement = cell.getElement();
    el.classList.add('format-boolean');
    el.classList.toggle('is-default', isDef);
  }

  rowStateFormatter = (row) => {
    const data = row.getData();

    // existing row color logic
    if (data.rowState == 2) row.getElement().style.backgroundColor = "#B8FFCC";
    if (data.rowState == 3) row.getElement().style.backgroundColor = "#A6A6DF";
    if (data.rowState == 4) row.getElement().style.backgroundColor = "#FFBAAB";
    if (data.rowState == 5) row.getElement().style.backgroundColor = "#FF8FD2";

    const rowId = data?.id;
    row.getCells().forEach(cell => {
      const field = cell.getColumn()?.getField?.();
      const el = cell.getElement();
      const def = cell.getColumn()?.getDefinition?.();
      if (def?.columnDataType === 4) {
        el.classList.add('format-boolean');
      }
      const isDef = rowId && field && this.tableService.isDefault(rowId, field);
      el.classList.toggle('is-default', !!isDef);
      if (isDef) el.setAttribute('title', 'Default from schema');
      else el.removeAttribute('title');
    });
  };

  //creates headers and sort/editing type for cells under the given header
  buildHeaders(schema: TableSchema, tableId: string): any[] {
    var headers: any[] = [];
    headers.push({
      formatter: "rowSelection",
      headerSort: false,
      headerHozAlign: "center",
      hozAlign: "center",
      titleFormatter: (cell, formatterParams, onRendered) => {
        this.createAndAppendCheckbox(cell, onRendered);
      },
      titleFormatterParams: {
        rowRange: "active"
      }

    });
    schema.columns.forEach(c => {
      var h: any = {};
      h.title = c.alias;
      h.field = c.columnName;
      h.columnDataType = c.columnDataType;
      h.headerFilter = true;
      switch (c.columnDataType) {
        case 1: { //string
          h.headerFilter = "input";
          h.accessorDownload = this.nullToEmptyString;
          h.editable = this.allowEdit;
          h.headerFilterFunc = (headerValue, rowValue, rowData, filterParams) => {
            if (!this.validateTableBeforeAction()) return true;
            const isInUse = String(rowValue).toLowerCase().includes(String(headerValue).toLowerCase());
            this.filterStateService.updateFilterState(h.field, headerValue);
            this.filterIsActive = this.checkIfAnyFilterIsActive();
            return isInUse;
          };
          h.headerFilterEmptyCheck = (value) => {
            const isEmpty = value === "";
            this.filterStateService.updateFilterState(h.field, isEmpty ? null : value);
            this.filterIsActive = this.checkIfAnyFilterIsActive();
            return isEmpty;
          };
          h.headerClick = (e, column, cell, headerValue) => {
            setTimeout(() => {
              this.sorterFunction();
              this.filterIsActive = this.checkIfAnyFilterIsActive();
            }, 0);
          };
          break;
        }
        case 2: { // integer
          h.accessorDownload = this.nullToEmptyString;
          h.editable = this.allowEdit;
          h.headerFilter = "input";
          h.headerFilterFunc = (headerValue, rowValue) => {
            if (!this.validateTableBeforeAction()) return true;
            const isInUse = String(rowValue).includes(headerValue);
            this.filterStateService.updateFilterState(h.field, headerValue);
            this.filterIsActive = this.checkIfAnyFilterIsActive();
            return isInUse;
          };
          h.headerFilterEmptyCheck = (value) => {
            const isEmpty = value === "";
            this.filterStateService.updateFilterState(h.field, isEmpty ? null : value);
            this.filterIsActive = this.checkIfAnyFilterIsActive();
            return isEmpty;
          };
          h.headerClick = () => {
            setTimeout(() => {
              this.sorterFunction();
              this.filterIsActive = this.checkIfAnyFilterIsActive();
            }, 0);
          };
          h.editor = this.integerEditor;
          h.editorParams = { step: 1 };
          h.formatter = this.integerFormatter;
          h.validator = { type: "integer", parameters: { allowEmpty: true } };
          break;
        }
        case 3: { //decimal
          h.accessorDownload = this.nullToEmptyString;
          h.editable = this.allowEdit;
          h.headerFilter = "input";
          h.headerFilterFunc = (headerValue, rowValue, rowData, filterParams) => {
            if (!this.validateTableBeforeAction()) return true;
            const isInUse = String(rowValue).includes(headerValue);
            this.filterStateService.updateFilterState(h.field, headerValue);
            this.filterIsActive = this.checkIfAnyFilterIsActive();
            return isInUse;
          };
          h.headerClick = (e, column, cell, headerValue) => {
            setTimeout(() => {
              this.sorterFunction();
              this.filterIsActive = this.checkIfAnyFilterIsActive();
            }, 0);
          };
          h.headerFilterEmptyCheck = (value) => {
            const isEmpty = value === "";
            this.filterStateService.updateFilterState(h.field, isEmpty ? null : value);
            this.filterIsActive = this.checkIfAnyFilterIsActive();
            return isEmpty;
          };
          break;
        }
        case 4: { // boolean
          h.editable = false;
          h.hozAlign = "center";
          h.headerFilter = "tickCross";
          h.headerFilterParams = { tristate: true };
          h.headerFilterLiveFilter = true; 

          const toBool = (v: any): boolean | null => {
            if (v === true || v === false) return v;
            if (v === 1 || v === 0) return v === 1;
            const s = (v ?? "").toString().trim().toLowerCase();
            if (s === "true" || s === "yes") return true;
            if (s === "false" || s === "no") return false;
            return null; 
          };
          h.headerFilterEmptyCheck = (value) => {
            const hv = toBool(value);
            const empty = hv === null;
            this.filterStateService.updateFilterState(h.field, empty ? null : hv);
            this.filterIsActive = this.checkIfAnyFilterIsActive?.();
            return empty;
          };
          h.headerFilterFunc = (headerValue, rowValue) => {
            if (!this.validateTableBeforeAction()) return true;

            const hv = toBool(headerValue);
            if (hv === null) return true; 

            const rv = toBool(rowValue);
            this.filterStateService.updateFilterState(h.field, hv);
            this.filterIsActive = this.checkIfAnyFilterIsActive?.();
            return rv === null ? true : rv === hv;
          };
          h.formatter = (cell) => {
            const v = cell.getValue();
            const isTrue = v === true || v === 1 || String(v).toLowerCase() === "true";
            const span = document.createElement("span");
            span.className = isTrue ? "tick-asset-mask" : "cross-asset-mask";
            return span;
          };

          h.cellClick = (_e, cell) => {
            if (this.allowEdit) {
              const cur = toBool(cell.getValue());
              const curBool = cur === true;
              cell.setValue(!curBool);
              this.applyBooleanDefaultClass(cell);
            }
          };

          break;
        }
        case 5: { //date
          h.headerFilter = "input";
          h.headerFilterLiveFilter = true;
          h.sorter = "date";
          h.editable = this.allowEdit;
          h.headerFilterFunc = (headerValue, rowValue) => {
            if (!this.validateTableBeforeAction()) return true;

            this.filterStateService.updateFilterState(h.field, headerValue);
            this.filterIsActive = this.checkIfAnyFilterIsActive();

            return this.dateMatchesFilter(headerValue, rowValue);
          };
          h.headerClick = (e, column, cell, headerValue) => {
            setTimeout(() => {
              this.sorterFunction();
              this.filterIsActive = this.checkIfAnyFilterIsActive();
            }, 0);
          };
          h.headerFilterEmptyCheck = (value) => {
            const isEmpty = value === "";
            this.filterStateService.updateFilterState(h.field, isEmpty ? null : value);
            this.filterIsActive = this.checkIfAnyFilterIsActive();
            return isEmpty;
          };
          break;
        }
      }
      const dataSetAccess = this.myAccess?.dataSets?.find(d => d.dataSetId === tableId) ??
        { create: false, update: false, delete: false, read: true };
      // sets what buttons are shown base on permission
      this.addRowShow = dataSetAccess.create;
      this.duplicateSelectedRowsShow = dataSetAccess.create;
      this.deleteSelectedRowsShow = dataSetAccess.delete;
      this.undeleteSelectedRowsShow = dataSetAccess.delete;
      this.editSelectedRowsShow = dataSetAccess.update;
      var tableEdit = this.allowEdit
      if (dataSetAccess.update == true || dataSetAccess.create == true || dataSetAccess.delete == true) {
        this.saveShow = true;
      } else {
        this.saveShow = false;
      }
      if (dataSetAccess?.update || dataSetAccess?.create) {
        //set editors
        if (Array.isArray(c.listItems) && c.listItems.length > 0) {
          h.editor = this.selectEditor;
          h.editorParams = { values: c.listItems };
          h.headerFilter = "select";
          h.headerFilterParams = { values: c.listItems };
          // Keep local filter state in sync and use equality (matches your server `eq`)
          h.headerFilterFunc = (headerValue, rowValue) => {
            if (!this.validateTableBeforeAction()) return true;
            const hv = headerValue == null ? "" : String(headerValue);
            const rv = rowValue == null ? "" : String(rowValue);
            this.filterStateService.updateFilterState(h.field, hv || null);
            this.filterIsActive = this.checkIfAnyFilterIsActive();
            return hv === "" ? true : rv === hv;  
          };

          h.headerFilterEmptyCheck = (value) => {
            const isEmpty = value === "" || value == null;
            this.filterStateService.updateFilterState(h.field, isEmpty ? null : value);
            this.filterIsActive = this.checkIfAnyFilterIsActive();
            return isEmpty; 
          };
        } else {
          switch (c.columnDataType) {
            case 1: { //string
              h.editor = "input";
              h.headerFilter = "input";
              break;
            }
            case 2: { //integer
              h.editor = this.integerEditor;
              h.editorParams =
              {
                step: 1
              };
              h.formatter = this.integerFormatter;
              h.validator = {
                type: "integer",
                parameters: {
                  allowEmpty: true
                }
              }
              break;
            }
            case 3: { //decimal
              h.editor = this.decimalEditor;
              h.headerFilter = "input";

              break;
            }
            case 4: { //boolean
              h.editable = false;
              h.hozAlign = "center";
              h.cssClass = "format-boolean";
              break;
            }
            case 5: { //date
              h.editor = this.dateEditorV2;
              h.formatter = this.dateFormatter;
              h.accessorDownload = this.downloadDate;
              h.validator = this.dateValidator;
              h.sorter = this.dateSorter;
              break;
            }

            default: {
              break;
            }
          }
        }
      } else if (dataSetAccess?.read) {
        // READ-ONLY
        //Use select + equality (match server `eq`)
        //Sync FilterStateService; emptyCheck returns boolean
        //Only for listItems; booleans handled earlier
        if (Array.isArray(c.listItems) && c.listItems.length > 0) {
          h.headerFilter = "select";
          h.headerFilterParams = { values: c.listItems };
          h.headerFilterFunc = (headerValue, rowValue) => {
            if (!this.validateTableBeforeAction()) return true;

            const hv = headerValue == null ? "" : String(headerValue);
            const rv = rowValue == null ? "" : String(rowValue);

            this.filterStateService.updateFilterState(h.field, hv || null);
            this.filterIsActive = this.checkIfAnyFilterIsActive();

            return hv === "" ? true : rv === hv;
          };

          h.headerFilterEmptyCheck = (value) => {
            const isEmpty = value === "" || value == null;
            this.filterStateService.updateFilterState(h.field, isEmpty ? null : value);
            this.filterIsActive = this.checkIfAnyFilterIsActive();
            return isEmpty;
          };
        } else {
          switch (c.columnDataType) {
            case 4: { // boolean
              break;
            }
            case 5: { // date
              h.formatter = this.dateFormatter;
              h.accessorDownload = this.downloadDate;
              break;
            }
            default: {
              break;
            }
          }
        }
      } else {
      }

      //set sorters
      h.headerSortTristate = true;
      switch (c.columnDataType) {
        case 1: { //string
          h.sorter = "string";
          break;
        }
        case 2: { //integer
          h.sorter = "number";
          break;
        }
        case 3: { //decimal
          h.sorter = "number";
          break;
        }
        case 4: { //boolean
          h.sorter = "boolean";
          break;
        }
        case 5: { //date
          h.sorter = this.dateSorter;
          break;
        }
        default: {
          break;
        }
      }
      const col = c;                        
      const baseFormatter = h.formatter;
        if (!this.shouldAutoRestore(col)) {
          h.formatter = (cell, formatterParams, onRendered) => {
            const svc: any = this.tableService as any;
            const parseDef = svc.parseDefaultForColumnPublic?.bind(svc);
            const equalsDef = svc.equalsDefaultPublic?.bind(svc);
            const v = cell.getValue();
            const def = parseDef ? parseDef(col) : null;
            const hasDefault = def !== null;
            const isNullish = v === null || v === undefined || v === "";
            const isEqual = equalsDef ? equalsDef(col, v) : false;
            let out: any;
            if (hasDefault && (isNullish || isEqual)) {
              if (typeof baseFormatter === "function") {
                const originalGet = cell.getValue.bind(cell);
                (cell as any).getValue = () => def;
                out = baseFormatter(cell, formatterParams, onRendered);
                (cell as any).getValue = originalGet;
              } else {
                const span = document.createElement('span');
                span.textContent = String(def ?? '');
                out = span;
              }
            } else {
              out = (typeof baseFormatter === "function")
                ? baseFormatter(cell, formatterParams, onRendered)
                : (baseFormatter ?? v);
            }
            const rowId = cell?.getRow()?.getData()?.id;
            const field = cell.getColumn()?.getField?.();
            const el = cell.getElement();
            el.classList.remove("is-default");
            el.removeAttribute("title");

            if (rowId && field && this.tableService.isDefault(rowId, field)) {
              el.classList.add("is-default");
              el.setAttribute("title", "Default from schema");
            }
            return out;
          };
        }
      headers.push(h);
    });
    return headers;
  }

  // renders Invalid Request if decimal is used in integer
  integerFormatter(cell, formatterParams, onRendered) {
    const el = cell.getElement();
    el.classList.remove('invalid-integer'); 
    const v = cell.getValue();

    if (v === null || v === undefined || (typeof v === 'string' && v.trim() === '')) {
      return '';
    }

    const n = Number(v);
    if (Number.isInteger(n)) {

      return n;
    } else {
      el.classList.add('invalid-integer');
      return 'INVALID INTEGER';
    }
  }

  async downloadCsv(): Promise<void> {
    if (!this.validateTableBeforeAction()) return;
    const sanitizeFileName = (name: string) =>
      (name.replace(/[^a-zA-Z0-9-_\.]/g, '_')
           .replace(/_+/g, '_')
           .replace(/^_+|_+$/g, '')
           .substring(0, 100)) || 'download';
    const safeAlias = sanitizeFileName(this.tableInfo.alias || 'download');
    try {
      const blob = await this.tableService.downloadFile(
        this.tableInfo.id,
        safeAlias,
        this.filterParams,
        this.stringLocalizerService.currentCulture
      );
      const a = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      a.setAttribute('rel', 'noopener noreferrer');
      a.href = objectUrl;
      a.download = `${safeAlias}.csv`;
      a.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      this.logger.error('TabulatorUserTableComponent', 'Error downloading CSV', undefined, {
        operation: 'downloadCsv',
        tableId: this.tableInfo?.id
      });
    }
  }

  //When downloading turns null values into empty strings
  nullToEmptyString = function (value) {
    return value ?? ""; 
  }

  // when downloading changes the date to common format.
  downloadDate = function (value) {
    if (value == null || value === "" || value === "(mm/dd/yyyy)") return "";
    const m = moment(value, ["YYYY-MM-DDTHH:mm:ss", "MM/DD/YYYY"], true);
    return m.isValid() ? m.format("MM/DD/YYYY") : "INVALID DATE";
  }

  updateRowStateCounts() {
    // Assuming this.tabRows contains all rows and their current state
    const counts = { edited: 0, new: 0, deleted: 0 };
    this.tableService.myTableChanges.editedRows.rows.currentChanges.forEach(row => {
      switch (row.rowState) {
        case 3: counts.edited++; break;
        case 2: counts.new++; break;
        case 4:
        case 5: counts.deleted++; break;
      }
    });
    this.rowStateCounts.next(counts);
  }

  selectEditor = function (cell, onRendered, success, cancel, editorParams) {
    // Create and style editor
    const editor = document.createElement("select");
    editor.style.padding = "3px";
    editor.style.width = "100%";
    editor.style.boxSizing = "border-box";
    if (editorParams && Array.isArray(editorParams.values)) {
      editorParams.values.forEach(value => {
        const option = document.createElement("option");
        option.value = value;
        option.text = value;
        editor.appendChild(option);
      });
    } else {
      this.logger.warn('TabulatorUserTableComponent', 'Select editor values were missing or invalid', {
        operation: 'selectEditor'
      });
    }
    const cellValue = cell.getValue();
    if (cellValue != null) {
      editor.value = cellValue;
    }
    onRendered(function () {
      editor.focus();
      editor.style.height = "100%";
    });
    editor.addEventListener("change", function () {
      success(editor.value);
    });
    editor.addEventListener("blur", function () {
      cancel();
    });
    return editor;
  };

// Accepts: MM/DD/YYYY, MM-DD-YYYY, MMDDYYYY, MMDDYY,
// optionally with time: HH:mm or HH:mm:ss (space separated).
// Stores ISO; while editing, shows just MM/DD/YYYY so it never "flips" format.
dateEditorV2 = (cell, onRendered, success, cancel) => {
  const input = document.createElement("input");
  input.type = "text";
  input.placeholder = "(mm/dd/yyyy)";
  input.inputMode = "numeric";
  input.autocomplete = "off";
  input.style.padding = "3px";
  input.style.width = "100%";
  input.style.boxSizing = "border-box";
  const cur = cell.getValue();
  const mCur = (window as any).moment(
    cur,
    [
      "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD",
      "MM/DD/YYYY", "MM-DD-YYYY",
      "MM/DD/YYYY HH:mm", "MM/DD/YYYY HH:mm:ss",
      "MM-DD-YYYY HH:mm", "MM-DD-YYYY HH:mm:ss"
    ],
    true
  );
  input.value = mCur.isValid() ? mCur.format("MM/DD/YYYY") : "";

  onRendered(() => {
    input.focus();
    input.select();
    input.style.height = "100%";
  });

  const commit = () => {
    let raw = (input.value ?? "").trim();
    if (raw === "" || raw === "(mm/dd/yyyy)") {
      success(null);
      return;
    }
    const digits = raw.replace(/\D/g, "");
    if (/^\d{8}$/.test(digits)) {
      raw = `${digits.slice(0,2)}/${digits.slice(2,4)}/${digits.slice(4)}`;
    } else if (/^\d{6}$/.test(digits)) {
      raw = `${digits.slice(0,2)}/${digits.slice(2,4)}/${digits.slice(4)}`;
    }

    // Accept date-only or date+time
    const formats = [
      "MM/DD/YYYY HH:mm:ss", "MM/DD/YYYY HH:mm",
      "MM-DD-YYYY HH:mm:ss", "MM-DD-YYYY HH:mm",
      "MM/DD/YYYY", "MM-DD-YYYY",
      "MM/DD/YY",  "MM-DD-YY"
    ];
    const m = (window as any).moment(raw, formats, true);

    if (!m.isValid()) {
      cell.validate();
      cancel();
      return;
    }

    // Keep time iff present in the user's raw input
    const hasTime = /\s+\d{1,2}:\d{2}/.test(raw);
    const iso = (hasTime ? m : m.startOf("day")).format("YYYY-MM-DD[T]HH:mm:ss");

    success(iso);
  };

  input.addEventListener("blur", commit);
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") commit();
    if (e.key === "Escape") cancel();
  });

  return input;
};

  integerEditor = (cell, onRendered, success, cancel) => {
    const input = document.createElement("input");
    input.type = "number";
    input.step = "1";
    input.style.padding = "3px";
    input.style.width = "100%";
    input.style.boxSizing = "border-box";

    const cur = cell.getValue();
    input.value = (cur == null ? "" : String(cur));

    onRendered(() => {
      input.focus();
      input.select();
      input.style.height = "100%";
    });

    const commit = () => {
      const raw = input.value.trim();
      if (raw === "") return success(null);
      const n = Number(raw);
      Number.isInteger(n) ? success(n) : cancel();
    };

    input.addEventListener("blur", commit);
    input.addEventListener("keydown", e => {
      if (e.key === "Enter") commit();
      if (e.key === "Escape") cancel();
    });
    return input;
  };

decimalEditor = (cell, onRendered, success, cancel) => {
  const columnName = cell.getColumn().getField();
  const col = this.colByName.get(columnName);
  const scale = col?.decimals ?? 0;

  const input = document.createElement("input");
  input.type = "number";
  input.step = String(1 / Math.max(1, Math.pow(10, Math.min(scale, 10))));
  input.style.padding = "3px";
  input.style.width = "100%";
  input.style.boxSizing = "border-box";
  input.value = cell.getValue() == null ? "" : String(cell.getValue());

  onRendered(() => { input.focus(); input.select(); input.style.height = "100%"; });

  const commit = () => {
    const raw = input.value.trim();
    if (raw === "") return success(null);
    const n = this.truncateToScale(raw, scale);   // <-- no rounding, just cut extras
    if (!Number.isFinite(n)) return cancel();
    success(n);
  };

  input.addEventListener("blur", commit);
  input.addEventListener("keydown", e => {
    if (e.key === "Enter") commit();
    if (e.key === "Escape") cancel();
  });

  return input;
};

dateFormatter = (cell) => {
  const v = cell.getValue();
  if (v == null || v === "" || v === "(mm/dd/yyyy)") {
    cell.getElement().style.color = "#e0e0eb";
    return "(mm/dd/yyyy)";
  }

  const m = (window as any).moment(
    v,
    [
      "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD",
      "MM/DD/YYYY", "MM-DD-YYYY",
      "MM/DD/YYYY HH:mm", "MM/DD/YYYY HH:mm:ss",
      "MM-DD-YYYY HH:mm", "MM-DD-YYYY HH:mm:ss"
    ],
    true
  );

  if (m.isValid()) {
    cell.getElement().style.color = ""; 
    return m.format("MM/DD/YYYY");
  }

  cell.getElement().style.color = "#FF0000";
  return "INVALID DATE";
};

  private parseSchemaDateDefault(raw: any): string | null {
    const s = (raw ?? '').toString().trim();
    if (!s) return null;
    const formats = [
      "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD",
      "MM/DD/YYYY", "MM-DD-YYYY",
      "MM/DD/YY", "MM-DD-YY",
      "MMDDYYYY", "MMDDYY",
      "YY-MM-DD", "YYYY/MM/DD",
      "M/D/YY", "M-D-YY", "M/D/YYYY", "M-D-YYYY"
    ];
    const m = (window as any).moment(s, formats, false);
    return m.isValid() ? m.startOf('day').format("YYYY-MM-DD[T]HH:mm:ss") : null;
  }

  private normalizeDateToIso(v: any): string | null {
    const s = (v ?? '').toString().trim();
    if (!s) return null;
    const formats = [
      "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD",
      "MM/DD/YYYY", "MM-DD-YYYY",
      "MM/DD/YY", "MM-DD-YY",
      "MMDDYYYY", "MMDDYY",
      "YY-MM-DD", "YYYY/MM/DD",
      "M/D/YY", "M-D-YY", "M/D/YYYY", "M-D-YYYY"
    ];
    const m = (window as any).moment(s, formats, false);
    return m.isValid() ? m.startOf('day').format("YYYY-MM-DD[T]HH:mm:ss") : null;
  }

  dateValidator = (cell, value) => {
    if (!value || value === "(mm/dd/yyyy)") return true;
    const m = (window as any).moment(
      value,
      [
        "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD",
        "MM/DD/YYYY", "MM-DD-YYYY",
        "MM/DD/YYYY HH:mm", "MM/DD/YYYY HH:mm:ss",
        "MM-DD-YYYY HH:mm", "MM-DD-YYYY HH:mm:ss"
      ],
      true
    );
    return m.isValid();
  };

  dateSorter = function (a, b) {
    const parse = (val: any): number => {
      if (val == null || val === "") return 0;
      const m = (window as any).moment(
        val,
        [
          "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD",
          "MM/DD/YYYY", "MM-DD-YYYY",
          "MM/DD/YYYY HH:mm", "MM/DD/YYYY HH:mm:ss",
          "MM-DD-YYYY HH:mm", "MM-DD-YYYY HH:mm:ss"
        ],
        true
      );
      return m.isValid() ? m.valueOf() : -1; 
    };
    return parse(a) - parse(b);
  };


  // adds an empty row to the table, and assigns any specified default values to their respective cells.
  addRow() {
    const newRow = new TableRow();
    this.schema.columns.forEach(c => {
      const v = (this.tableService as any).parseDefaultForColumnPublic?.(c) ?? null;
      newRow[c.columnName] = v;
    });
    newRow.DISTransactionId = this.generateUUID();
    newRow.id = newRow.DISTransactionId;
    newRow.rowState = 2;
    this.schema.columns.forEach(c => {
      const v = newRow[c.columnName];
      if ((v ?? "") !== "") this.tableService.markDefault(newRow.id, c.columnName);
    });

    this.tableService.addTableRow(newRow, this.tableInfo["id"]);
    this.tableService.myTableChanges.editRow(newRow);

    this.updateFilters();
    this.table.addRow(newRow).then(row => this.table.deselectRow(row._row.data.id));
    this.updateRowStateCounts();
  }

  // creates a copy of any selected rows, assigns those rows new ID's, and adds those rows to the table
  duplicateSelectedRows() {
    const selectedRows = this.table.getSelectedRows();
    let rowsToDuplicate: any[] = [];

    if (selectedRows.length > 0) {
      rowsToDuplicate = selectedRows
        .map(row => row.getData())
        .filter(r => r.rowState !== 4 && r.rowState !== 5);
    } else if (this.tableService.rowChanges.value.length > 0) {
      rowsToDuplicate = this.tableService.rowChanges.value
        .map(row => row._row?.data || row)
        .filter(r => r.rowState !== 4 && r.rowState !== 5);
    }

    rowsToDuplicate.forEach(sourceRow => {
      const newRow: any = {};
      this.schema.columns.forEach(c => { newRow[c.columnName] = sourceRow[c.columnName]; });
      Object.keys(sourceRow).forEach(key => {
        if (key === '__df' || key.startsWith('_')) return;
        if (!['rowState', 'id', 'DISTransactionId'].includes(key) && !(key in newRow)) {
          newRow[key] = sourceRow[key];
        }
      });

      newRow["DISTransactionId"] = this.generateUUID();
      newRow["id"] = newRow["DISTransactionId"];
      newRow["rowState"] = 2;

      // Mark and normalize defaults on the clone so the UI shows them as defaults
      const svc: any = this.tableService as any;
      const parseDef = svc.parseDefaultForColumnPublic?.bind(svc);
      const equalsDef = svc.equalsDefaultPublic?.bind(svc);
      this.schema.columns.forEach(c => {
        const v = newRow[c.columnName];
        const def = parseDef?.(c);
        const isDefault = def !== null && equalsDef?.(c, v);
        if (isDefault) {
          // Normalize dates to parsed default to keep formatting consistent
          if (c.columnDataType === 5 && def !== null) {
            newRow[c.columnName] = def;
          }
          this.tableService.markDefault(newRow.id, c.columnName);
        } else {
          this.tableService.clearDefault(newRow.id, c.columnName);
        }
      });
      this.tableService.addCopyTableRow(newRow, this.tableInfo.id);
      this.updateFilters();
      this.table.addRow(newRow).then(row => this.table.deselectRow(row._row.data.id));
    });

    this.updateRowStateCounts();
  }


  deleteSelectedRows() {
    this.showDeleted = false;
    this.showNewDeleted = false;
    let selectedRows = this.table.getSelectedRows();
    let rowChanges = this.tableService.rowChanges.getValue();
    rowChanges.forEach(rowChange => {
      this.tableService.myTableChanges.editRow(rowChange._row.data);
    });
    // Existing logic to process selected rows
    selectedRows.forEach(row => {
      this.tableService.myTableChanges.editRow(row.getData());
    });
    this.tableService.myTableChanges.editedRows.rows.currentChanges.forEach((r, key, map) => {
      let isRNotInDontDelete = rowChanges.some(rowComponent => rowComponent._row.data.id === r.id);
      if (isRNotInDontDelete) {
        switch (r["rowState"]) {
          case 0:
          case 1:
          case 3:
            r["rowState"] = 4;
            this.updateFiltersAndScrollToRow(r);
            break;
          case 2:
            r["rowState"] = 5;
            this.updateFiltersAndScrollToRow(r);
            break;
        }
      }
    });
    this.updateFilters();
  }
  updateFiltersAndScrollToRow(r) {
    this.updateFilters();
    let rowToUpdate = this.table.getRow(r.id);
    if (rowToUpdate) {
      rowToUpdate.rowState = r.rowState;
      rowToUpdate.getElement().style.backgroundColor = r.rowState == 4 ? "#FFBAAB" : "#FF8FD2";
      this.table.deselectRow(rowToUpdate);
    }
    this.tableService.removeRowById(r.id);
    this.updateRowStateCounts();
  }
  undeleteSelectedRows() {
    let changes = this.tableService.myTableChanges;
    changes.editedRows.rows.previousChanges.forEach((r, key) => {
      if (r.rowState == 5) {
        //Change "New Deleted" to "New"
        r.rowState = 2;
        this.table.deselectRow(r.id);
      } else if (r.rowState == 4) {
        var changedCells = [];
        var initialValues = this.tableService.getInitialValues(r.id, this.tableInfo.id);
        for (let key in r) {
          let value = r[key];
          if (!key.startsWith("_") && value != "rowState") {
            if (initialValues && initialValues.id != r.id) {
              changedCells.push(value);
            }
          }
        }
        if (changedCells.length >= 1) {
          r.changedCells = changedCells;
        }
        if (r.changedCells && r.changedCells.length >= 1) {
          r.rowState = 3;
        } else {
          r.rowState = 1;
        }
      }
      changes.editedRows.rows.set(key, r);
      let existingRow = this.table.getRow(r.id);
      if (existingRow) {
        existingRow.update(r);
      }
      if (r.rowState == 1) {
        let row = this.table.getRow(r.id);
        if (row) {
          row._row.data.rowState = 1;
          row.getElement().style.backgroundColor = "";
        }
      }
      if (r.rowState == 2) {
        let row = this.table.getRow(r.id);
        if (row) {
          row._row.data.rowState = 2;
        }
      }
      if (r.rowState == 3) {
        let row = this.table.getRow(r.id);
        if (row) {
          row._row.data.rowState = 3;
        }
      }
      this.table.deselectRow(r.id);
    });
    this.updateFilters();
    this.updateRowStateCounts();
  }

  private generateUUID(): string { // Public Domain/MIT
    var d = new Date().getTime();//Timestamp
    var d2 = (performance && performance.now && (performance.now() * 1000)) || 0;
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16;
      if (d > 0) {
        r = (d + r) % 16 | 0;
        d = Math.floor(d / 16);
      } else {
        r = (d2 + r) % 16 | 0;
        d2 = Math.floor(d2 / 16);
      }
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }

  public editRow(rowData: any) {
    this.tableService.editRow(rowData);
    this.updateRowStateCounts();
  }

  public async saveTableChanges() {
    if (!this.validateTableBeforeAction()) return;

    const selectedRows = this.table.getSelectedRows() || [];
    selectedRows.forEach(row => this.tableService.myTableChanges.editRow(row.getData()));
    const rows = this.table.getRows().filter(r => r._row.data.rowState >= 2 && r._row.data.rowState <= 5);
    try {
      const success = await this.tableService.updateTableRows(this.tableInfo.id);

      this.table.deselectRow();
      rows.forEach(row => {
        const d = row && row._row && row._row.data;
        if (!d) return;

        // Only assign DISTransactionId for NEW rows (rowState === 2)
        if (d.rowState === 2 && !d['DISTransactionId']) {
          d['DISTransactionId'] = d['id'];
        }
      });

      if (success) {
        const deletedIndexes: string[] = [];
        const upsertedIndexes: string[] = [];
        rows.forEach(row => {
          switch (row._row.data["rowState"]) {
            case 2:
            case 3:
              upsertedIndexes.push(row.getIndex());
              break;
            case 4:
            case 5:
              deletedIndexes.push(row.getIndex());
              break;
          }
        });

        this.commitUpsertedRows(upsertedIndexes);
        this.commitDeletedRows(deletedIndexes);

        this.tableService.resetChangeTracking(this.tableInfo["id"]);
        this.rowStateCounts.next({ edited: 0, new: 0, deleted: 0 });
        this.$rowChangesLength.next(0);

        this.toastService.showSuccessToast('', 'Saving Complete!');
      } else {
        this.logger.error('TabulatorUserTableComponent', 'Failed to save data', undefined, {
          operation: 'saveTableChanges',
          tableId: this.tableInfo?.id
        });
        this.toastService.showErrorToast('Save Failed', '');
      }
    } catch (error) {
      this.logger.error('TabulatorUserTableComponent', 'Failed to save data', error, {
        operation: 'saveTableChanges',
        tableId: this.tableInfo?.id
      });
      this.toastService.showErrorToast('Save Failed', '');
    }
  }

  private commitUpsertedRows(newIndexes: string[]) {
    const resetBaselines = (rowComponent: any) => {
      rowComponent.getCells().forEach(c => {
        const v = c.getValue();
        c._cell.saveBaseline = v;
        c.valueAtLoad = v;
        c._cell.initialValue = v;
      });
    };

    newIndexes.forEach(index => {
      const row = this.table.getRow(index);
      if (row) {
        row._row.data["rowState"] = 1;
        row.getElement().style.backgroundColor = "";
        row._row.changedCells = [];
        resetBaselines(row);
      }
    });
  }

  private commitDeletedRows(deletedIndexes: string[]) {
    deletedIndexes.forEach(index => {
      this.table.deleteRow(index);
    });
  }

  // FILTER CONTROLS

  // toggle visibility of unedited, non-new records
  public toggleOriginalRecordsFilter() {
    this.showOriginal = !this.showOriginal;
    this.showOriginalToolTipKey = this.showOriginal ? "hideOriginalRecords" : "showOriginalRecords";
    this.updateFilters();
  }

  public toggleEditedOriginalRecordsFilter() {
    if (this.tableService.myTableChanges && this.tableService.myTableChanges.editedRows) {
      this.tableService.myTableChanges.editedRows.rows.currentChanges.forEach((value, key) => {
        if (value.rowState === 3) {
          var row = this.table.getRow(value.id);
          if (this.table.getRow(value.id)) {
            let rowChanges = this.tableService.rowChanges.getValue();
            for (let rowChange of rowChanges) {
              let dataId = rowChange._row.data.id;
              let rowId = value.id;
              if (dataId === rowId) {
                row.select();
              }
            }
          } else {
            this.table.addData(value);
          }
          let rowChanges = this.tableService.rowChanges.getValue();

          for (let rowChange of rowChanges) {
            let dataId = rowChange._row.data.id;
            let rowId = value.id;
            if (dataId === rowId) {
              if (row != false) {
                row.select();
              }
            }
          }
        }
      });
    }
    this.showChangedToolTipKey = this.showChanged ? "hideEditedOriginalRecords" : "showEditedOriginalRecords";
    this.showChanged = !this.showChanged;
    this.updateFilters();
  }

  // toggle visibility of newly created records
  public toggleNewRecordsFilter() {
    this.showNew = !this.showNew;
    this.showNewToolTipKey = this.showNew ? "hideNewRecords" : "showNewRecords";
    this.updateFilters();
  }

  // toggle visibility of deleted, non-new records
  public toggleDeletedRecordsFilter() {
    if (this.tableService.myTableChanges && this.tableService.myTableChanges.editedRows) {
      this.tableService.myTableChanges.editedRows.rows.currentChanges.forEach((value, key) => {
        if (value.rowState === 5 || value.rowState === 4) {
          var row = this.table.getRow(value.id);
          if (!row) {
            this.table.addData(value);
          }
        }
      });
    }
    this.showDeleted = !this.showDeleted;
    this.showNewDeleted = !this.showNewDeleted;
    this.showDeletedToolTipKey = this.showDeleted ? "hideDeletedRecords" : "showDeletedRecords";
    this.updateFilters();
  }
  public toggleSelectedRecordsFilter() {
    if (this.showSelectedRecords === false) {
      this.tableService.rowChanges.value.forEach(value => {
        var row = this.table.getRow(value._row.data.id);
        if (!row) {
          this.table.addData(value._row.data);
          this.table.selectRow(value._row.data.id);
          this.showSelectedRecords = true;

        }
      });
    } else {
      const selectedRows = this.table.getSelectedRows();
      selectedRows.forEach(row => {
        this.table.deleteRow(row.getData().id);
      });
      this.showSelectedRecords = false;
    }
    // Toggle the state
    this.showNewDeletedToolTipKey = this.showSelectedRecords ? "hideDeletedNewRecords" : "showDeletedNewRecords";
    this.updateFilters();
  }

  // triggers table filters to go into effect
  private updateFilters() {
    var rowStateFilter = [];
    if (this.showOriginal) { rowStateFilter.push(0); rowStateFilter.push(1) }
    if (this.showNew) { rowStateFilter.push(2) }
    if (this.showChanged) { rowStateFilter.push(3) }
    if (this.showDeleted) { rowStateFilter.push(4) }
    if (this.showNewDeleted) { rowStateFilter.push(5) }
    if (rowStateFilter.length == 0) { rowStateFilter.push(-1) }
    this.table.setFilter("rowState", "in", rowStateFilter);
  }

  openModal(id: string) {
    this.modalService.open(id);
  }

  closeModal(id: string) {
    this.modalService.close(id);
  }
  nextPage() {
    if (!this.validateTableBeforeAction()) return;
    this.currentPage++;
    this.showSelectedRecords = false;
    this.tableService.loadTablePage(this.tableInfo.id, this.currentPage, this.pageSize, this.filterParams, this.sorter)
  .then((data) => {
     if (data) {
       this.setDataWithDefaults(data.rows);
       this.updatePageRange();
       this.reselectChangedRows();
     }
   });
  }

  prevPage() {
    if (!this.validateTableBeforeAction()) return;
    if (this.currentPage > 1) {
      this.showSelectedRecords = false;
      this.currentPage--;
      this.tableService.loadTablePage(this.tableInfo.id, this.currentPage, this.pageSize, this.filterParams, this.sorter)
        .then((data) => {
          if (data) {
            this.setDataWithDefaults(data.rows);
            this.updatePageRange();
            this.reselectChangedRows();
          }
        });
    }
  }

  private updatePageRange() {
    this.totalPages = this.tableService.totalPages;
    let start = Math.max(this.currentPage - 2, 1);
    let end = Math.min(start + 4, this.totalPages);
    start = Math.max(end - 4, 1);
    this.pageRange = [];
    for (let i = start; i <= end; i++) {
      this.pageRange.push(i);
    }
  }

  updatePageSize(newSize: number) {
    if (!this.validateTableBeforeAction()) return;
    this.currentPage = 1;
    this.pageSize = newSize;
    this.showSelectedRecords = false;
    this.updatePageRange();
    this.tableService.loadTablePage(this.tableInfo.id, this.currentPage, this.pageSize, this.filterParams, this.sorter)
      .then((data) => {
        this.setDataWithDefaults(data.rows);
        this.updatePageRange();
        this.reselectChangedRows();
      });
  }

  sorterFunction() {
    if (!this.validateTableBeforeAction()) return;
    this.filterStateService.updateSorterState(true);
    let sortParams = [];
    let currentSorters = this.table.getSorters();
    this.showSelectedRecords = false;
    if (currentSorters) {
      currentSorters.forEach(sorter => {
        if (sorter.dir === "desc") {
          sortParams.push(`${sorter.field} d`);
        } else if (sorter.dir === "asc") {
          sortParams.push(`${sorter.field} a`);
        }
      });
    }

    let sortParam = sortParams.join(";");
    this.sorter = sortParam;
    this.tableService.loadTablePage(this.tableInfo.id, this.currentPage, this.pageSize, this.filterParams, this.sorter)
      .then((data) => {
        if (data) {
          this.setDataWithDefaults(data.rows);
          this.reselectChangedRows();
        }
      });
  }

  async searchAll(): Promise<void> {
    if (!this.validateTableBeforeAction()) return;
    const filters: string[] = [];
    const sortParams: string[] = [];
    const active = this.table.getHeaderFilters() || [];

    const sanitizeFreeText = (raw: string) =>
      raw
        .replace(/[^\w\s.\-]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 200);
    for (const hf of active) {
      const field = hf?.field;
      if (!field) continue;

      const col = this.table.getColumn(field);
      const def = col?.getDefinition?.() || {};
      let v = hf.value;
      if (v === '' || v === null || typeof v === 'undefined') continue;

      const isBool = def.columnDataType === 4 || def.headerFilter === 'tickCross' || def.headerFilter === 'tick';
      if (isBool) {
        const s = String(v).trim().toLowerCase();
        if (['true', '1'].includes(s)) { filters.push(`${field} eq true`); }
        else if (['false', '0'].includes(s)) { filters.push(`${field} eq false`); }
        continue;
      }

      const isSelect =
        def.headerFilter === 'select' ||
        (def.headerFilterParams && Array.isArray(def.headerFilterParams.values));
      // For dates, we want to allow the user to input in their preferred format but convert to ISO for the server filter
      const isDate = def.columnDataType === 5;

      const raw = String(v).trim();
      if (isSelect) {
        const allowed: string[] = Array.isArray(def.headerFilterParams?.values)
          ? def.headerFilterParams.values.map(String)
          : [];
        if (!allowed.includes(raw)) continue;
        filters.push(`${field} eq ${raw}`);
      } else if (isDate) {
        const safeDate = this.normalizeDateFilterForServer(raw);
        if (!safeDate) continue;
        filters.push(`${field} lk ${safeDate}`);
      } else {
        const safe = sanitizeFreeText(raw);
        if (!safe) continue;
        filters.push(`${field} lk ${safe}`);
      }
    }

    for (const s of this.table.getSorters()) {
      if (!s.field) continue;
      sortParams.push(`${s.field} ${s.dir === 'desc' ? 'd' : 'a'}`);
    }

    const hasNewHeaderValues = filters.length > 0;
    const nextFilterParams = hasNewHeaderValues ? filters.join(';') : this.filterParams;
    const nextSorter = sortParams.join(';');

    if (nextFilterParams === this.filterParams && nextSorter === this.sorter) {
      this.filterIsActive = !!(this.filterParams || this.sorter);
      return;
    }

    this.filterParams = nextFilterParams;
    this.sorter = nextSorter;
    this.lastCommittedHeaderFilters = this.currentHeaderFilterMap();
    this.filterIsActive = !!(this.filterParams || this.sorter);
    this.currentPage = 1;

    const data = await this.tableService.loadTablePage(
      this.tableInfo.id, this.currentPage, this.pageSize, this.filterParams, this.sorter
    );
    if (data) {
      this.setDataWithDefaults(data.rows);
      this.updatePageRange();
      this.reselectChangedRows();
    }
  }

  async clearFilters(): Promise<void> {
    if (!this.validateTableBeforeAction()) return;

    this.table.clearHeaderFilter();
    this.table.clearSort();
    this.headerFilterValues = [];
    this.filterIsActive = false;
    this.filterStateService.updateSorterState(false);
    this.filterStateService.clearFilterState();
    this.filterParams = "";
    this.sorter = "";
    this.lastCommittedHeaderFilters = {};  
    this.showSelectedRecords = false;

    const data = await this.tableService.loadTablePage(
      this.tableInfo.id, this.currentPage, this.pageSize, null, null
    );
    if (data) {
      this.setDataWithDefaults(data.rows);
      this.updatePageRange();
      this.reselectChangedRows();
    }
  }
  // Return current header-filter values as a simple {field: value} map
  private currentHeaderFilterMap(): Record<string, any> {
    if (!this.table) return {};
    const out: Record<string, any> = {};

    for (const hf of this.table.getHeaderFilters() || []) {
      const field = hf?.field;
      if (!field) continue;

      const col = this.table.getColumn(field);
      const def = col?.getDefinition?.() || {};
      let v = hf.value;
      if (v === '' || v === null || typeof v === 'undefined') continue;
      const isBool = def.columnDataType === 4 || def.headerFilter === 'tickCross' || def.headerFilter === 'tick';
      if (isBool) {
        const s = String(v).trim().toLowerCase();
        if (['true', '1', 'yes'].includes(s)) v = true;
        else if (['false', '0', 'no'].includes(s)) v = false;
        else continue;
      }

      out[field] = v;
    }
    return out;
  }

  // True only if current (non-empty) header filters differ from lastCommittedHeaderFilters.
  get canSearchAll(): boolean {
    const now = this.currentHeaderFilterMap();
    const hasAny = Object.keys(now).length > 0;

    const keys = (o: Record<string, any>) => Object.keys(o).sort();
    const eq = (a: Record<string, any>, b: Record<string, any>) =>
      keys(a).length === keys(b).length &&
      keys(a).every((k, i) => k === keys(b)[i] && String(a[k]) === String(b[k]));

    const changed = !eq(now, this.lastCommittedHeaderFilters);
    return hasAny && changed;
  }

  // Clear Filters stays ON if there's any committed server baseline, any local filters, or a sorter
  get canClearFilters(): boolean {
    const localFiltersActive = Object.values(this.filterStateService.getFilterState())
      .some(v => v !== null && v !== '');
    const sorterApplied = this.filterStateService.isSorterApplied();
    const hasCommittedServer = !!(this.filterParams || this.sorter);
    return localFiltersActive || sorterApplied || hasCommittedServer;
  }

  async loadPage(page: number): Promise<void> {
    if (!this.validateTableBeforeAction()) return;

    this.currentPage = page;
    this.showSelectedRecords = false;

    const data = await this.tableService.loadTablePage(
      this.tableInfo.id,
      this.currentPage,
      this.pageSize,
      this.filterParams,
      this.sorter
    );

    if (data) {
      this.setDataWithDefaults(data.rows);
      this.updatePageRange();
      this.reselectChangedRows();
    }
  }

  private validateTableBeforeAction(): boolean {
    const validationResult = this.table.validate();
    const isValid = validationResult === true;
    this.tableService.setTableValidState(isValid);
    if (validationResult !== true) {
      this.toastService.showErrorToast('Invalid data detected', '');
      return false;
    }
    return true;
  }

  private truncateToScale(input: string | number, scale: number): number {
    if (input === null || input === undefined || input === "") return NaN as any;

    const s = String(input).trim();
    const m = s.match(/^([+-]?)(\d+)(?:\.(\d+))?$/);
    if (!m) return Number(input);

    const sign = m[1] ?? "";
    const intPart = m[2] ?? "0";
    const frac = (m[3] ?? "").slice(0, Math.max(0, scale));
    const out = scale > 0 && frac.length > 0 ? `${sign}${intPart}.${frac}` : `${sign}${intPart}`;
    return Number(out);
  }

  private recalculateAllowEdit(): void {
    this.allowEdit = false;
    if (!this.tableInfo || !this.myAccess) return;
    const ds = this.myAccess.dataSets?.find(d => d.dataSetId === this.tableInfo.id);
    this.allowEdit = !!(ds && (ds.create || ds.update || ds.delete));
  }
  
  private reselectChangedRows(): void {
    const changes = this.tableService.rowChanges.value;
    if (!changes?.length) return;
    const ids = new Set(
      changes.map(r => r && r._row && r._row.data && r._row.data.id).filter(Boolean)
    );
    this.table.getRows().forEach(row => {
      const id = row.getData().id;
      if (ids.has(id)) row.select();
    });
  }

  private applySchemaDefaultsToRows(rows: any[]): any[] {
  if (!Array.isArray(rows) || !this.schema?.columns?.length) return rows ?? [];

  const svc: any = this.tableService as any;
  const parseDef = typeof svc.parseDefaultForColumnPublic === 'function'
    ? (c: any) => svc.parseDefaultForColumnPublic(c)
    : (c: any) => {
        switch (c.columnDataType) {
          case 4: return Parser.parseBoolean(String(c.defaultValue ?? ""));
          case 2:
          case 3: return (c.defaultValue ?? "") !== "" ? Number(c.defaultValue) : null;
          case 5: return this.parseSchemaDateDefault(c.defaultValue);
          default: return c.defaultValue;
        }
      };

  const equalsDef = typeof svc.equalsDefaultPublic === 'function'
    ? (c: any, v: any) => svc.equalsDefaultPublic(c, v)
    : (c: any, v: any) => {
        switch (c.columnDataType) {
          case 2:
          case 3: return Number(v) === Number(parseDef(c));
          case 4: return Parser.parseBoolean(String(v ?? "")) === Parser.parseBoolean(String(parseDef(c) ?? ""));
          case 5: {
            const a = this.normalizeDateToIso(v);
            const b = parseDef(c);
            return !!a && !!b && a === b;
          }
          default: return (v ?? "").toString().trim() === (parseDef(c) ?? "").toString().trim();
        }
      };

    return rows.map(src => {
      const r: any = { ...src };
      const rowId = r.id;

      for (const c of this.schema!.columns) {
        const cur = r[c.columnName];
        const def = parseDef(c);
        const hasDefault = def !== null;

        // treat "" as nullish for booleans so defaults apply
        const isNullish = cur === null || typeof cur === 'undefined' || (c.columnDataType === 4 && cur === "");

        if (isNullish) {
          if (hasDefault) {
            r[c.columnName] = def;
            if (rowId) this.tableService.markDefault(rowId, c.columnName);
          }
          continue;
        }
        if (hasDefault && equalsDef(c, cur)) {
          if (rowId) this.tableService.markDefault(rowId, c.columnName);
          if (c.columnDataType === 5) {
            r[c.columnName] = def;
          }
        }
      }
      return r;
    });
  }

  private shouldAutoRestore(col: { columnDataType: number; required?: boolean }) {
    return !!col.required;
  }

  private setDataWithDefaults(rows: any[]): void {
    const transformed = this.applySchemaDefaultsToRows(rows ?? []);
    this.tabRows = transformed;
    if (this.table) {
      this.table.setData(transformed);
    } else {
      this.tabRows = transformed;
      this.drawTable();
    }
  }

  private seedDefaultRegistryFromInitial(table: { rows?: any[]; schema: { columns: any[] } }) {
    this.tableService.clearAllDefaults?.();
    const cols = table?.schema?.columns ?? [];
    const rows = table?.rows ?? [];
    const parseDef = (c: any) => this.tableService.parseDefaultForColumnPublic(c);
    const equalsDef = (c: any, v: any) => this.tableService.equalsDefaultPublic(c, v);

    rows.forEach(r => {
      const rowId = r?.id;
      if (!rowId) return;

      cols.forEach(c => {
        const hasKey = Object.prototype.hasOwnProperty.call(r, c.columnName);
        const v = hasKey ? r[c.columnName] : null;
        const def = parseDef(c);       
        const hasDefault = def !== null;
        const isNullish = v === null || v === undefined || v === "";
        if (hasDefault && (isNullish || equalsDef(c, v))) {
          this.tableService.markDefault(rowId, c.columnName);
        }
      });
    });
  }

  private buildDateSearchAliases(value: any): string[] {
    if (value == null || value === "") return [];

    const m = (window as any).moment(value, this.DATE_FILTER_FORMATS, false);
    if (!m.isValid()) return [String(value).toLowerCase()];

    return [
      m.format("MM/DD/YYYY").toLowerCase(),
      m.format("M/D/YYYY").toLowerCase(),
      m.format("MM/DD/YY").toLowerCase(),
      m.format("M/D/YY").toLowerCase(),
      m.format("MM-DD-YYYY").toLowerCase(),
      m.format("M-D-YYYY").toLowerCase(),
      m.format("MM-DD-YY").toLowerCase(),
      m.format("M-D-YY").toLowerCase(),
      m.format("YYYY-MM-DD").toLowerCase(),
      m.format("YYYY-MM").toLowerCase(),
      m.format("YYYY").toLowerCase(),
      m.format("MM/DD").toLowerCase(),
      m.format("M/D").toLowerCase(),
      m.format("MM/").toLowerCase(),
      m.format("M/").toLowerCase(),
      m.format("MM-DD").toLowerCase(),
      m.format("M-D").toLowerCase(),
      m.format("MM-").toLowerCase(),
      m.format("M-").toLowerCase(),
    ];
  }

  // filtering local mactching 
  private dateMatchesFilter(headerValue: any, rowValue: any): boolean {
    const raw = String(headerValue ?? "").trim().toLowerCase();
    if (!raw) return true;

    const aliases = this.buildDateSearchAliases(rowValue);
    if (!aliases.length) return false;

    return aliases.some(a => a.includes(raw));
  }

  // Server query string creation for dat filtering
  private normalizeDateFilterForServer(raw: string): string | null {
    const v = String(raw ?? "").trim();
    if (!v) return null;

    // Keep these formats exactly (they already match ISO stored values)
    if (/^\d{4}-\d{2}$/.test(v)) return v; 
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v; 
    if (/^\d{4}$/.test(v)) return v;               

    const pad2 = (s: string) => s.padStart(2, '0').slice(-2);
    const inRange = (n: number, min: number, max: number) => Number.isFinite(n) && n >= min && n <= max;

    //Treat as month (01-12) consistency with local filtering.
    if (/^\d{1,2}$/.test(v)) {
      const monthNum = Number(v);
      if (!inRange(monthNum, 1, 12)) return null;
      return `-${pad2(v)}-`;
    }
    
    const mdPartial = v.match(/^(\d{1,2})[\/\-](\d{0,2})$/);
    if (mdPartial) {
      const monthNum = Number(mdPartial[1]);
      if (!inRange(monthNum, 1, 12)) return null;

      const mm = pad2(mdPartial[1]);
      const ddRaw = mdPartial[2];
      if (!ddRaw) return `-${mm}-`;

      const dayNum = Number(ddRaw);
      if (!inRange(dayNum, 1, 31)) return null;

      return `-${mm}-${pad2(ddRaw)}`;
    }

    // Full displayed dates -> normalize to ISO
    const m = (window as any).moment(v, [
      "MM/DD/YYYY", "M/D/YYYY",
      "MM-DD-YYYY", "M-D-YYYY",
      "MM/DD/YY", "M/D/YY",
      "MM-DD-YY", "M-D-YY"
    ], true);
    if (m.isValid()) return m.format("YYYY-MM-DD");

    return null;
  }
}